<template>
  <div :class="{'groups-scroll-container': shouldUseGroupsScrollContainer}">
    <v-layout
      column
      class="px-2 pb-2"
    >
      <!-- Buttons to jump between groups -->
      <v-layout
        ref="groupscontainer"
        :class="{'groups-scroll-container': shouldUseGroupsScrollContainer}"
        :wrap="!shouldUseGroupsScrollContainer"
      >
        <v-btn
          v-for="(group, groupIndex) in nonDefaultGroups"
          v-show="
            hideFlaggedGroupsDuringActivation
              ? !group.hide_during_activation
              : true
          "
          :key="'group_' + groupIndex"
          ref="groupbuttons"
          flat
          class="overlay-options-fields-group-btn mx-0"
          :class="[
            groupIndex === selectedGroupIndex
              ? groupButtonSelectedClasses
              : groupButtonUnselectedClasses,

            useSmallGroupButtons ? 'mb-3' : 'mb-4',
          ]"
          :small="useSmallGroupButtons"
          :style="{'min-width': useSmallGroupButtons ? '0' : 'initial'}"
          @click="onGroupButtonClicked(groupIndex)"
        >
          <v-badge
            color="error"
            :value="atLeastOneFieldInvalidInGroup(groupIndex)"
          >
            <v-icon
              slot="badge"
              :title="$t('tvtemplatesEditOverlayDataValidationError')"
            >
              priority_high
            </v-icon>
            {{ group.label }}
          </v-badge>
        </v-btn>
      </v-layout>
      <!-- Render fields/widget for the selected group -->
      <v-layout wrap>
        <!-- v-show needed since we pull the overlayData from the 'living' widgets! -->
        <template
          v-for="(fieldOrWidget, renderableIndex) in renderablesSortedByGroup"
        >
          <!-- Widget -->
          <overlay-data-widget
            v-if="
              isWidget(fieldOrWidget.name) &&
                isTopLevelWidget(fieldOrWidget.name)
            "
            v-show="isWidgetFromSelectedGroup(fieldOrWidget.name)"
            ref="widgets"
            :key="'widget_' + fieldOrWidget.name"
            :color-palette="colorPalette"
            :extra-warnings="extraWarnings"
            :is-field="isField"
            :is-widget="isWidget"
            :renderable-index="renderableIndex"
            :widget="fieldOrWidget"
            style="width: 100%"
            @changed="onFieldChanged"
            @change-image="$emit('change-image', $event)"
          />
          <!-- Field -->
          <overlay-data-field
            v-else-if="
              isField(fieldOrWidget.name) &&
                isNotFieldInWidget(fieldOrWidget.name)
            "
            v-show="isFieldFromSelectedGroup(fieldOrWidget.name)"
            ref="fields"
            :key="'grouped_fields_' + renderableIndex"
            :field="fieldOrWidget"
            :warning="getExtraWarningForField(fieldOrWidget)"
            :color-palette="colorPalette"
            class="overlay-data-field px-2"
            :class="{
              text: fieldOrWidget.data.kind === 'TEXT',
              price: fieldOrWidget.data.kind === 'PRICE',
              'one-column':
                fieldOrWidget.data.one_column || $vuetify.breakpoint.xsOnly,
            }"
            :style="fieldOrWidget.data.css ? fieldOrWidget.data.css.form : null"
            @changed="onFieldChanged"
            @change-image="$emit('change-image', $event)"
          />
        </template>
      </v-layout>
    </v-layout>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import {deepCopyObject} from '../utils';

import {default as defaultColorPalette} from '@/mixins/colorPalette.js';
import overlayDataExtraWarnings from '@/mixins/overlayDataExtraWarnings.js';
import {OverlayOptionsField} from '@/mixins/overlayOptions.js';

import OverlayDataField from '@/components/OverlayDataField';
import OverlayDataWidget from '@/components/OverlayDataWidget';

export default {
  components: {
    OverlayDataField,
    OverlayDataWidget,
  },
  mixins: [overlayDataExtraWarnings],
  props: {
    /**
     * An object to validate User input.
     */
    overlayOptions: {
      type: Object,
      required: true,
      validator(options) {
        return (
          options.dynamic_fields &&
          options.dynamic_fields.schema &&
          options.dynamic_fields.data
        );
      },
    },
    /**
     * Optional object containing overlay_data.
     *
     * Will be used to populate the field widgets if specified, e.g.
     * when editing existing data instead of creating new one.
     */
    overlayData: {
      type: Object,
      default: null,
    },
    /**
     * Optional list of prepared warning objects for specific fields.
     *
     * These will be shown as orange text below the field interface.
     *
     * Example:
     *
     *   [
     *     {
     *       'fieldLabel': String,
     *       'fieldName': String,
     *       'prefix': String,
     *     },
     *     ...
     *   ]
     */
    extraWarnings: {
      type: Array[Object],
      default: () => [],
    },
    /**
     * Whether to validate fields and show errors directly after start.
     *
     * We possibly want to avoid this when creating new fields: Since
     * they are empty, the are very likely invalid, but there is no need
     * to rub it in the User's face. However when editing, we want
     * this to make sure the action buttons are active.
     */
    validateOnStart: {
      type: Boolean,
      default: false,
    },
    colorPalette: {
      type: Array[String],
      default: () => defaultColorPalette,
    },
    /**
     * Hide Groups marked as .hide_during_activation in UI.
     *
     * The DOM elements will still exist and react to changing Branding
     * etc., use default_value and submit their value, they will just
     * not be visible and this editable by the User.
     *
     * We implemented this on the .groups level instead of individual
     * fields as we don't have to fix partial/broken widgets then.
     */
    hideFlaggedGroupsDuringActivation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allFieldsAreValid: false,
      invalidGroupIndices: [],
      selectedGroupIndex: null,
    };
  },
  computed: {
    schema() {
      return this.overlayOptions.dynamic_fields.schema;
    },
    data() {
      return this.overlayOptions.dynamic_fields.data;
    },
    /**
     * The implicit or explicit default order of fields/widgets by name.
     *
     * The default_group is useful to define the order of fields/widgets
     * without having to use the tabs of actual groups.
     *
     * Affected by (last one wins):
     *
     *   - Order of field/widget definition in data
     *   - Value of optional .order attribute per field
     *   - Order of field/widget names in optional default_group
     */
    defaultGroup() {
      const nonWidgetFieldNames = this.renderableFieldsSortedByOrder.map(
        (field) => field.name
      );
      let fieldAndWidgetNames = this.widgetNames.concat(nonWidgetFieldNames);

      // Use explicit default_group field order, if existing. Ignore bogus names.
      if (this.overlayOptions.dynamic_fields.default_group) {
        fieldAndWidgetNames =
          this.overlayOptions.dynamic_fields.default_group.filter((name) =>
            fieldAndWidgetNames.includes(name)
          );
      }

      return {
        isDefault: true,
        label: '',
        fields: fieldAndWidgetNames,
      };
    },
    groups() {
      return (
        (this.overlayOptions.dynamic_fields || {}).groups || [this.defaultGroup]
      );
    },
    nonDefaultGroups() {
      return this.groups.filter((group) => !group.isDefault);
    },
    widgets() {
      return this.overlayOptions.dynamic_fields.widgets || [];
    },
    widgetNames() {
      return this.widgets.map((widget) => widget.name);
    },
    usesWidgets() {
      return this.widgets.length > 0;
    },
    allWidgetFieldNames() {
      let fieldNames = [];

      for (const widget of this.widgets) {
        const isInterfaceWidget =
          !!widget.field_mapping &&
          Object.keys(widget.field_mapping).length > 0;
        const isLayoutWidget = !!widget.columns;

        if (isInterfaceWidget) {
          const mappingFieldNames = Object.values(widget.field_mapping);
          fieldNames = fieldNames.concat(mappingFieldNames);
        } else if (isLayoutWidget) {
          for (const col of widget.columns || []) {
            const colFieldNames = col.filter((name) => !!name);
            fieldNames = fieldNames.concat(colFieldNames);
          }
        }
      }
      return fieldNames;
    },
    selectedGroup() {
      return this.groups[this.selectedGroupIndex];
    },
    staticFields() {
      return this.overlayOptions.static_fields;
    },
    /**
     * Aggregate object for each property, containing its schema and data.
     *
     * Bonus hint: We can manually add add/edit the 'order' in the
     *   overlay_options data to force a particular field ordering.
     */
    fields() {
      const fields = [];
      Object.keys(this.data).forEach((fieldName) => {
        const fieldRequired = this.schema.required.indexOf(fieldName) !== -1;
        const fieldSchema = this.schema.properties[fieldName];
        const fieldData = this.data[fieldName];

        if (!fieldData) {
          console.error('No data found for property:', fieldName);
        }
        if (!fieldSchema) {
          // Header's are only for UI structuring, they are allowed to
          // have no schema, but for other fields it's an error.
          if (fieldData.kind !== 'HEADER') {
            console.error('No schema found for property:', fieldName);
          }
        }

        let fieldValue = null;
        if (this.overlayData) {
          const currentValue = this.overlayData[fieldName];
          fieldValue = currentValue;
        }

        let render = true;
        if (this.usesWidgets && this.allWidgetFieldNames.includes(fieldName)) {
          // Skip individual interface creation for fields used in a widget.
          render = false;
        }

        const field = new OverlayOptionsField({
          name: fieldName,
          data: fieldData,
          schema: fieldSchema || {}, // HEADER fields have no schema
          value: fieldValue,
          required: fieldRequired,
          render: render,
        });

        fields.push(field);
      });

      return fields;
    },
    fieldNames() {
      return this.fields.map((field) => field.name);
    },
    fieldNameToField() {
      const nameToField = {};
      for (const field of this.fields) {
        nameToField[field.name] = field;
      }
      return nameToField;
    },
    widgetNameToWidget() {
      const nameToWidget = {};
      for (const widget of this.widgets) {
        nameToWidget[widget.name] = widget;
      }
      return nameToWidget;
    },
    fieldsSortedByOrder() {
      return orderBy(this.fields, 'data.order');
    },
    renderableFieldsSortedByOrder() {
      return this.fieldsSortedByOrder.filter((field) => field.render);
    },
    selectedGroupFields() {
      if (!this.selectedGroup) {
        return [];
      }
      return this.fields.filter((field) => {
        return this.selectedGroup.fields.indexOf(field.name) !== -1;
      });
    },
    selectedGroupWidgets() {
      if (!this.selectedGroup) {
        return [];
      }
      return this.widgets.filter((widget) => {
        return this.selectedGroup.fields.indexOf(widget.name) !== -1;
      });
    },
    groupButtonSelectedClasses() {
      return 'msr--text selected';
    },
    groupButtonUnselectedClasses() {
      return 'grey--text text--darken-1';
    },
    shouldUseGroupsScrollContainer() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    useSmallGroupButtons() {
      return this.$vuetify.breakpoint.xs;
    },
    /**
     * Associate each field with a group index.
     *
     * If a group mentions a widget name instead this will be expanded
     * to the included field names automatically.
     */
    fieldOrWidgetNameToGroupIndex() {
      const nameToGroupIndex = {};
      for (let groupIndex = 0; groupIndex < this.groups.length; groupIndex++) {
        const group = this.groups[groupIndex];
        for (const renderableName of group.fields) {
          if (this.isField(renderableName)) {
            const fieldName = renderableName;
            nameToGroupIndex[fieldName] = groupIndex;
          } else if (this.isWidget(renderableName)) {
            nameToGroupIndex[renderableName] = groupIndex;

            const widgetFieldNames = Object.values(
              this.widgets.filter((w) => w.name == renderableName)[0]
                .field_mapping
            );
            for (const widgetFieldName of widgetFieldNames) {
              nameToGroupIndex[widgetFieldName] = groupIndex;
            }
          }
        }
      }
      return nameToGroupIndex;
    },
    /**
     * Sort by index of group, then index of field, then optional order attribute.
     *
     * That way the group buttons and field widgets will be ordered the
     * same way as in the overlay_options JSON and we can control it by
     * reordering in there intuitively.
     */
    fieldOrWidgetNameToGroupSortKey() {
      // https://stackoverflow.com/a/1306751
      const numPaddingDigitsGroups = Math.ceil(
        Math.log10(this.groups.length + 1)
      );

      const nameToSortKey = {};
      for (let groupIndex = 0; groupIndex < this.groups.length; groupIndex++) {
        const group = this.groups[groupIndex];
        const numPaddingDigitsGroupFields = Math.ceil(
          Math.log10(group.fields.length + 1)
        );
        for (
          let fieldIndex = 0;
          fieldIndex < group.fields.length;
          fieldIndex++
        ) {
          const fieldName = group.fields[fieldIndex];
          // We need to zero-pad the numbers so digits are the same and
          // ordering them works as expected, otherwise '012' < '02' etc.
          const sortKey =
            String(groupIndex).padStart(numPaddingDigitsGroups, '0') +
            ' ' +
            String(fieldIndex).padStart(numPaddingDigitsGroupFields, '0');
          nameToSortKey[fieldName] = sortKey;
        }
      }
      return nameToSortKey;
    },
    fieldsSortedByGroup() {
      for (let field of this.fields) {
        field.groupSortKey = this.fieldOrWidgetNameToGroupSortKey[field.name];
      }
      return orderBy(this.fields, 'groupSortKey');
    },
    renderableFieldsSortedByGroup() {
      return this.fieldsSortedByGroup.filter((field) => !!field.render);
    },
    widgetsSortedByGroup() {
      for (let widget of this.widgets) {
        widget.groupSortKey = this.fieldOrWidgetNameToGroupSortKey[widget.name];
      }
      return orderBy(this.widgets, 'groupSortKey');
    },
    /**
     * Widgets with expanded field data, which it needs to render UI for them.
     *
     * For this we replace the values of the widget's field_mapping with
     * actual field objects (instead of just names).
     */
    renderableWidgetsSortedByGroup() {
      if (!this.widgetsSortedByGroup) {
        return [];
      }
      return this.widgetsSortedByGroup.map((widget) => {
        const copiedWidget = deepCopyObject(widget);

        // Expand fields mentioned in a widget's field_mapping.
        this.expandWidgetFieldMapping(copiedWidget);

        // Expand fields of specific layout widgets.
        //
        // Note: Layout widgets can contain non-layout widgets, not
        //   just fields!
        //
        //   - WIDGET_LAYOUT_COLUMNS: Expand field/widget names in .columns
        //
        for (const col of copiedWidget.columns || []) {
          for (let i = 0; i < col.length; i++) {
            const fieldOrWidgetName = col[i];
            const colField = this.fieldNameToField[fieldOrWidgetName];
            const colWidget = this.widgetNameToWidget[fieldOrWidgetName];
            if (colField) {
              col[i] = colField;
            } else if (colWidget) {
              const copiedColWidget = deepCopyObject(colWidget);
              this.expandWidgetFieldMapping(copiedColWidget);
              col[i] = copiedColWidget;
            }
          }
        }

        return copiedWidget;
      });
    },
    /**
     * Combines list of fields and widgets to render.
     *
     * We combine it here for sorting and grouping, then let the
     * template handle how to render it.
     */
    renderablesSortedByGroup() {
      const renderablesWithGroupSortKey =
        this.renderableFieldsSortedByGroup.concat(
          this.renderableWidgetsSortedByGroup
        );
      return orderBy(renderablesWithGroupSortKey, 'groupSortKey');
    },
    selectedGroupFieldAndWidgetNames() {
      const groupWidgetNames = this.selectedGroupWidgets.map(
        (widget) => widget.name
      );
      const groupFieldNames = this.selectedGroupFields.map(
        (field) => field.name
      );
      return groupWidgetNames.concat(groupFieldNames);
    },
  },
  watch: {
    /**
     * React to changed IMAGE/TEXT default_values and propagate as needed.
     *
     * This can happen e.g. by resolving a dynamic Branch-specific IMAGE
     * default_value via {branchimage_id: <id>}. We then need to tell
     * the corresponding field to re-render itself to see the resolved
     * image.
     */
    overlayOptions: function (overlayOptions, oldOverlayOptions) {
      this.updateFieldsWhereBranchSpecificDefaultValueChanged(
        overlayOptions,
        oldOverlayOptions
      );
    },
    allFieldsAreValid: function (valid) {
      this.$emit('valid', valid);
    },
  },
  mounted() {
    this.setInitialSelectedGroupIndex();

    if (this.validateOnStart) {
      this.$nextTick(() => {
        this.checkIfAllFieldsAreValid();
      });
    }
  },
  methods: {
    setInitialSelectedGroupIndex() {
      if (!this.hideFlaggedGroupsDuringActivation) {
        this.selectedGroupIndex = 0;
        return;
      }
      const selectableGroups = this.groups.filter(
        (group) => !group.hide_during_activation
      );
      if (!selectableGroups.length) {
        return;
      }
      for (let index = 0; index < this.groups.length; index++) {
        const group = this.groups[index];
        if (group.label == selectableGroups[0].label) {
          this.selectedGroupIndex = index;
          break;
        }
      }
    },
    expandWidgetFieldMapping(widget) {
      if (!widget.field_mapping) {
        return;
      }
      const expandedFieldMapping = {};
      for (const internalFieldName of Object.keys(widget.field_mapping)) {
        const fieldName = widget.field_mapping[internalFieldName];
        expandedFieldMapping[internalFieldName] =
          this.fieldNameToField[fieldName];
      }
      widget.field_mapping = expandedFieldMapping;
    },
    updateFieldsWhereBranchSpecificDefaultValueChanged(
      overlayOptions,
      oldOverlayOptions
    ) {
      if (
        !overlayOptions ||
        !overlayOptions.dynamic_fields ||
        !overlayOptions.dynamic_fields.data
      ) {
        return;
      }
      if (
        !oldOverlayOptions ||
        !oldOverlayOptions.dynamic_fields ||
        !oldOverlayOptions.dynamic_fields.data
      ) {
        return;
      }
      for (const fieldName of Object.keys(overlayOptions.dynamic_fields.data)) {
        const field = overlayOptions.dynamic_fields.data[fieldName];
        if (field.kind === 'IMAGE' || field.kind === 'TEXT') {
          const oldField = oldOverlayOptions.dynamic_fields.data[fieldName];
          const defaultHasChanged =
            oldField.default_value != field.default_value;
          if (defaultHasChanged) {
            console.log(
              '[INFO] ' + fieldName + ' default has changed, rerendering...'
            );
            this.$bus.$emit(
              'set-overlay-data-field-value',
              fieldName,
              field.default_value
            );
          }
        }
      }
    },
    onGroupButtonClicked(groupIndex) {
      this.selectedGroupIndex = groupIndex;
    },
    atLeastOneFieldInvalidInGroup(groupIndex) {
      return this.invalidGroupIndices.indexOf(groupIndex) !== -1;
    },
    isWidget(renderableName) {
      return this.widgetNames.includes(renderableName);
    },
    isField(renderableName) {
      return (
        this.fieldNames.includes(renderableName) &&
        !this.isWidget(renderableName)
      );
    },
    isNotFieldInWidget(fieldName) {
      return !this.allWidgetFieldNames.includes(fieldName);
    },
    isTopLevelWidget(widgetName) {
      // Ensure we are not looking a e.g. an interface widget nested
      // within a layout widget, since the validation is then handled by
      // the layou widget and we don't have to do this here again.
      return !this.allWidgetFieldNames.includes(widgetName);
    },
    isFieldFromSelectedGroup(renderableName) {
      return (
        this.isField(renderableName) &&
        this.selectedGroupFieldAndWidgetNames.indexOf(renderableName) !== -1
      );
    },
    isWidgetFromSelectedGroup(renderableName) {
      return (
        this.isWidget(renderableName) &&
        this.selectedGroupFieldAndWidgetNames.indexOf(renderableName) !== -1
      );
    },
    onFieldChanged() {
      this.checkIfAllFieldsAreValid();
      this.$emit('changed');
    },
    checkIfAllFieldsAreValid() {
      this.allFieldsAreValid = true;
      this.invalidGroupIndices = [];

      const fieldRefs = this.$refs.fields || [];
      for (const fieldRef of fieldRefs) {
        const isValidField = fieldRef.validate();
        if (!isValidField) {
          this.allFieldsAreValid = false;
          // Mark group with invalid field as faulty.
          const groupIndex =
            this.fieldOrWidgetNameToGroupIndex[fieldRef.field.name];
          this.invalidGroupIndices.push(groupIndex);
        }
      }

      const widgetRefs = this.$refs.widgets || [];
      for (const widgetRef of widgetRefs) {
        const isValidWidget = widgetRef.validate();
        if (!isValidWidget) {
          this.allFieldsAreValid = false;
          // Mark group with invalid widget as faulty.
          const groupIndex = this.fieldOrWidgetNameToGroupIndex[widgetRef.name];
          this.invalidGroupIndices.push(groupIndex);
        }
      }

      return this.allFieldsAreValid;
    },
    /**
     * Aggregate key-value pairs from User input.
     *
     * Values are read directly from field UI and widgets.
     *
     * An extra '_labels' field is added - this is somewhat redundant,
     * as the information is also in the overlay_options' schema, but:
     * We'd need an extra request just to get the label for display
     * later, so this is an optimization to spare us that. It gets
     * updated automatically on activation, so should propagate easily
     * if things change.
     */
    getOverlayData() {
      let overlayData = {
        _labels: {},
      };

      if (this.staticFields) {
        overlayData = {...overlayData, ...this.staticFields};
      }

      if (this.$refs.fields) {
        this.$refs.fields.forEach((fieldRef) => {
          const fieldData = fieldRef.getData();
          overlayData[fieldData.name] = fieldData.value;
          overlayData._labels[fieldRef.name] = fieldData.label;
        });
      }

      if (this.$refs.widgets) {
        this.$refs.widgets.forEach((widgetRef) => {
          const widgetOverlayData = widgetRef.getOverlayData();
          overlayData = {...overlayData, ...widgetOverlayData};
        });
      }

      return overlayData;
    },
  },
};
</script>

<style scoped>
.groups-scroll-container {
  overflow-x: auto;
}

/**
 * Customize horizontal scrollbar.
 *
 * See: https://www.w3schools.com/howto/howto_css_custom_scrollbar.asp
 */
.groups-scroll-container ::-webkit-scrollbar {
  height: 8px;
}
.groups-scroll-container ::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.groups-scroll-container ::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
}
.groups-scroll-container ::-webkit-scrollbar-thumb:hover {
  background: var(--msr-color-primary);
}

/* Group buttons */
.overlay-options-fields-group-btn {
  border: 1px solid transparent;
}
.overlay-options-fields-group-btn.selected {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  border-right: 1px solid rgba(0, 0, 0, 0.16);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  box-shadow: 3px 0 3px -4px rgba(0, 0, 0, 0.87);
}
.overlay-options-fields-group-btn:not(.selected) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0px;
}

.overlay-data-field {
  flex-grow: 1;
}
.overlay-data-field.text {
  width: 70%;
}
.overlay-data-field.price {
  width: 30%;
}
.overlay-data-field.one-column {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/OverlayData.vue